import clsx from 'clsx';
import React from 'react';
import {useParams, useSearchParams} from 'react-router-dom';
import {Body} from '@lightricks/react-design-system';
import {
  CONTENT_SUBMISSION_ANALYTICS_SCREENS as ANALYTICS_SCREENS,
  CONTENT_SUBMISSION_ANALYTICS_BUTTONS as ANALYTICS_BUTTONS,
} from '@/lib/analytics/analyticsConstants';
import translate from '@/utils/translate';
import ContentSubmissionPreview from '@/components/content-submission-preview';
import useTrackButtonEvent from '@/hooks/analytics/use-track-button-event/useTrackButtonEvent';
import useContentSubmissionQuery from '@/hooks/queries/use-content-submission-query';
import useDashboardUserQuery from '@/hooks/queries/use-dashboard-user-query';
import ContentSubmissionActionsFooter from '../components/content-submissions/content-submission-actions-footer';
import ContentSubmissionActivityFeed from '../components/content-submissions/content-submission-activity-feed';
import ContentSubmissionOutdatedBanner from '../components/content-submissions/content-submission-outdated-banner';
import ContentSubmissionSubHeader from '../components/content-submissions/content-submission-sub-header';
import styles from './ContentSubmissionView.module.scss';
import useAutoSelectLatestVersion from './useAutoSelectLatestVersion';

interface ContentSubmissionViewProps {
  isOffPlatform?: boolean;
}

function ContentSubmissionView({
  isOffPlatform = false,
}: ContentSubmissionViewProps) {
  const {campaignId, contentSubmissionId} = useParams<{
    campaignId: string;
    contentSubmissionId: string;
  }>();
  const [, setSearchParams] = useSearchParams();

  const {data: contentSubmission, isError} = useContentSubmissionQuery({
    contentSubmissionId,
    enabled: !!contentSubmissionId,
  });

  const {dashboardUser} = useDashboardUserQuery({});
  const isAdmin = dashboardUser?.isAdmin;

  const {pressed: pressedButton} = useTrackButtonEvent();

  const activeVersionId = useAutoSelectLatestVersion(
    contentSubmissionId,
    contentSubmission?.latestVersionId,
    contentSubmission?.versions?.map((version) => version.id)
  );

  const activeVersion = contentSubmission?.versions?.find(
    (v) => v.id === activeVersionId
  );

  const handleVersionChange = (versionId: string) => {
    setSearchParams((prev) => ({...Object.fromEntries(prev), versionId}), {
      replace: true,
    });
  };

  if (isError) {
    return <Body size="md">{translate('An unexpected error occurred')}</Body>;
  }

  if (!contentSubmission || !activeVersion) {
    return null;
  }

  const csContext = {
    campaign_id: campaignId,
    creator_id: contentSubmission.profile?.id,
    flow_id: contentSubmission.id,
  };

  const trackButton = (
    buttonName: string,
    extra: {tab?: string} = {}
  ): void => {
    pressedButton({
      button_name: buttonName,
      screen_name: ANALYTICS_SCREENS.ASSET_DETAIL,
      ...csContext,
      ...extra,
    });
  };

  return (
    <div className={clsx(styles.page, isOffPlatform && styles.pageOffPlatform)}>
      <ContentSubmissionSubHeader
        contentSubmission={contentSubmission}
        activeVersionId={activeVersionId}
        onVersionChange={(versionId) => {
          trackButton(ANALYTICS_BUTTONS.VERSION_SELECTOR);
          handleVersionChange(versionId);
        }}
      />
      <ContentSubmissionOutdatedBanner
        contentSubmission={contentSubmission}
        activeVersionId={activeVersionId}
        onVersionChange={(versionId) => {
          trackButton(ANALYTICS_BUTTONS.SWITCH_TO_LATEST_VERSION);
          handleVersionChange(versionId);
        }}
      />
      <div className={styles.mainContent}>
        <div className={styles.previewArea}>
          <ContentSubmissionPreview
            contentSubmission={contentSubmission}
            version={activeVersion}
            isOffPlatform={isOffPlatform}
            onNavigatePrevious={() =>
              trackButton(ANALYTICS_BUTTONS.NAV_PREVIOUS)
            }
            onNavigateNext={() => trackButton(ANALYTICS_BUTTONS.NAV_NEXT)}
          />
        </div>
        <div className={styles.feedArea}>
          <ContentSubmissionActivityFeed
            version={activeVersion}
            contentSubmission={contentSubmission}
            isOffPlatform={isOffPlatform}
            onCommentSubmit={() =>
              trackButton(ANALYTICS_BUTTONS.COMMENT_SUBMIT)
            }
            onActivityTabChange={(tab) =>
              trackButton(ANALYTICS_BUTTONS.ACTIVITY_TAB, {tab})
            }
          />
        </div>
      </div>
      <ContentSubmissionActionsFooter
        version={activeVersion}
        contentSubmission={contentSubmission}
        isOffPlatform={isOffPlatform}
        isAdmin={isAdmin}
        onAction={(actionName, context) =>
          pressedButton({
            button_name: actionName,
            screen_name: ANALYTICS_SCREENS.ASSET_DETAIL,
            ...context,
          })
        }
      />
    </div>
  );
}

export default ContentSubmissionView;
